import { Player } from '@lottiefiles/react-lottie-player';
import { navigate } from 'gatsby';
import React, { FC, ReactNode, useEffect } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled, { ThemeProvider } from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import FlowFooter from '@ui/atoms/FlowFooter';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';

import NewFlowHeader from '@ui/organisms/NewFlowHeader';
import PageContainer from '@ui/organisms/PageContainer';

import useMemberInfo, { IFragment } from '@hooks/useMemberInfo';

import { getCompletedHrtIntakes } from '@helpers/Intakes/intakes';

import { doesNotExist, exists } from '@utils/general';

import { IAuthenticatedMemberWithCompletedIntakes } from '@folx-types/member';

import PageLoader from '@assets/lottie/PageLoading.json';

import { intakes } from '@graphql/fragments/intakes';

import { getThemeForFlow } from '@theme/FlowThemes';
import theme from '@theme/FolxTheme';

interface IMemberGAHTConsultRouteProps {
	children: ReactNode;
	path?: string;
}

const MemberGAHTConsultLayout = styled(FlexColumn)`
	gap: ${theme.spacing.lg};
	max-width: 64rem;
	width: 100%;
`;

const StyledPageContainer = styled(PageContainer)`
	min-height: 100vh;
	background: ${theme.colors.white};
`;

const StyledRow = styled(FlexRow)`
	margin-top: ${theme.spacing.lgxl};
	padding: 0 ${theme.spacing.lg};
	overflow-x: hidden;
`;

const MemberGAHTConsultRoute: FC<IMemberGAHTConsultRouteProps> = ({ children }) => {
	const intakeFragment: IFragment = { name: 'Intakes', node: intakes };
	const { loading: memberLoading, memberInfo } =
		useMemberInfo<IAuthenticatedMemberWithCompletedIntakes>([intakeFragment]);

	useDeepCompareEffect(() => {
		if (exists(memberInfo)) {
			const completedIntakes = getCompletedHrtIntakes(memberInfo);
			if (exists(completedIntakes) && !location.state?.cogsworthId) {
				return navigate('/app/flows/followup');
			}
		}
	}, [memberLoading, memberInfo]);

	return (
		<ThemeProvider theme={getThemeForFlow('hrt_t')}>
			<StyledPageContainer
				header={<NewFlowHeader flowName="GAHT Consult" totalSteps={1} step={0} />}
				footer={<FlowFooter />}>
				<StyledRow>
					<LoadingWrapper
						loading={memberLoading}
						skeleton={<Player autoplay loop src={PageLoader} />}>
						<MemberGAHTConsultLayout>{children}</MemberGAHTConsultLayout>
					</LoadingWrapper>
				</StyledRow>
			</StyledPageContainer>
		</ThemeProvider>
	);
};

export default MemberGAHTConsultRoute;
